import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

export interface User {
  id?: number;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  image?: string;
  is_verified?: boolean;
  is_login_with_google?: boolean;
  phone?: string;
  location?: string;
  total_ads?: number;
  saved_items_count?: number;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (user: User, token: string, refreshToken?: string) => void;
  logout: () => void;
  updateToken: (newToken: string, newRefreshToken?: string) => void;
  getAuthHeaders: () => Record<string, string>;
  fetchProfile: () => Promise<User | null>;
  getDisplayName: () => string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initAuth = () => {
      try {
        const user = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refreshToken');

        if (user && token) {
          setAuthState({
            user: JSON.parse(user),
            token,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();
  }, []);

  // Login function - updates shared state
  const login = useCallback((user: User, token: string, refreshToken?: string) => {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', token);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    setAuthState({
      user,
      token,
      refreshToken: refreshToken || null,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');

    setAuthState({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  // Update token (for refresh)
  const updateToken = useCallback((newToken: string, newRefreshToken?: string) => {
    localStorage.setItem('token', newToken);
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }

    setAuthState(prev => ({
      ...prev,
      token: newToken,
      refreshToken: newRefreshToken || prev.refreshToken,
    }));
  }, []);

  // Get auth headers for API calls
  const getAuthHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Fetch profile from backend
  const fetchProfile = useCallback(async (): Promise<User | null> => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const API_URL = import.meta.env.VITE_API_URL;
      const response = await fetch(`${API_URL}/api/auth/profile/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 401) {
          logout();
        }
        return null;
      }

      const envelope = await response.json();
      // Accept both { success, data: { user } } and legacy { success, user }
      const ok = envelope?.success !== false;
      const userPayload = envelope?.data?.user ?? envelope?.user;
      if (ok && userPayload) {
        setAuthState(prev => ({
          ...prev,
          user: userPayload,
        }));
        localStorage.setItem('user', JSON.stringify(userPayload));
        return userPayload as User;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      return null;
    }
  }, [logout]);

  // Get display name for user
  const getDisplayName = useCallback(() => {
    if (!authState.user) return '';
    const { full_name, first_name, last_name, username } = authState.user;
    if (full_name) {
      return full_name;
    }
    if (first_name || last_name) {
      return `${first_name || ''} ${last_name || ''}`.trim();
    }
    return username || '';
  }, [authState.user]);

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    updateToken,
    getAuthHeaders,
    fetchProfile,
    getDisplayName,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export type { AuthContextType };
